import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { CalendarDays } from "lucide-react";
import { useAppointments } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { formatDateOnly } from "@/lib/utils";

type Row = Record<string, unknown>;

// sales.appointments.status enum (ADV-1143). "all" is the unfiltered sentinel
// that buildQuery() strips before it reaches the API.
const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No-show" },
  { value: "rescheduled", label: "Rescheduled" },
];

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "appointment_date",
    header: "Scheduled",
    cell: ({ row }) => {
      const date = row.original.appointment_date as string | undefined;
      const time = row.original.appointment_time as string | undefined;
      return (
        <div className="flex flex-col">
          <span className="font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
            {formatDateOnly(date)}
          </span>
          {time && (
            <span className="typo-label normal-case tracking-normal text-[color:var(--color-text-lo)]">
              {time}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "customer_name",
    header: "Customer",
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.customer_name as string | undefined;
      return (
        <span className={name ? "" : "text-[color:var(--color-text-lo)]"}>
          {name || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "opportunity_name",
    header: "Opportunity",
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.opportunity_name as string | undefined;
      return (
        <span className={name ? "" : "text-[color:var(--color-text-lo)]"}>
          {name || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status as string} />,
  },
  {
    accessorKey: "appointment_type",
    header: "Type",
    enableSorting: false,
    cell: ({ row }) => {
      const type = row.original.appointment_type as string | undefined;
      return (
        <span className={type ? "" : "text-[color:var(--color-text-lo)]"}>
          {type || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "assigned_rep_name",
    header: "Rep",
    enableSorting: false,
    cell: ({ row }) => {
      const rep = row.original.assigned_rep_name as string | undefined;
      return (
        <span className={rep ? "" : "text-[color:var(--color-text-lo)]"}>
          {rep || "—"}
        </span>
      );
    },
  },
];

function DateRangeFilter({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
}) {
  const active = from || to;
  const label =
    from && to
      ? `${formatDateOnly(from)} – ${formatDateOnly(to)}`
      : from
        ? `From ${formatDateOnly(from)}`
        : to
          ? `Until ${formatDateOnly(to)}`
          : "Date range";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={active ? "border-[color:var(--color-accent)]" : ""}
        >
          <CalendarDays className="size-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="appt-date-from" className="typo-label">
            From
          </Label>
          <Input
            id="appt-date-from"
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => onChange({ from: e.target.value, to })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="appt-date-to" className="typo-label">
            To
          </Label>
          <Input
            id="appt-date-to"
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => onChange({ from, to: e.target.value })}
          />
        </div>
        {active && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => onChange({ from: "", to: "" })}
          >
            Clear
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function V2AppointmentsPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "appointment_date", desc: true },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const [status, setStatus] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const pageSize = 50;

  const { data, isLoading } = useAppointments({
    offset: pageIndex * pageSize,
    limit: pageSize,
    status,
    date_from: dateRange.from,
    date_to: dateRange.to,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });

  const appointments = (data?.appointments ?? []) as Row[];
  const total = data?.total ?? appointments.length;

  // Filter changes invalidate the current page offset.
  const resetPage = () => setPageIndex(0);

  return (
    <ListPage
      title="Appointments"
      subtitle={`${total.toLocaleString()} total appointments`}
      filters={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ToggleGroup
            type="single"
            value={status}
            onValueChange={(v) => {
              // Radix clears the value when the active item is re-pressed —
              // fall back to "all" so the list never shows an empty filter.
              setStatus(v || "all");
              resetPage();
            }}
            className="justify-start"
          >
            {STATUS_FILTERS.map((s) => (
              <ToggleGroupItem key={s.value} value={s.value} size="sm">
                {s.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <DateRangeFilter
            from={dateRange.from}
            to={dateRange.to}
            onChange={(range) => {
              setDateRange(range);
              resetPage();
            }}
          />
        </div>
      }
    >
      <DataTable
        columns={columns}
        data={appointments}
        total={total}
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/appointments/${row.id}`)}
        emptyMessage="No appointments yet"
        emptyDescription="Appointments scheduled with customers appear here — they drive the activity on each opportunity."
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={setPageIndex}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </ListPage>
  );
}
